import type {
  ReportStrategyDetailResponse,
  ReportStrategyResponse,
  UpdateReportStrategyResult,
} from "../types/reports";
import type {
  ReportStrategyPineFields,
  StrategyActivePineSummary,
  StrategyActivePineStatus,
  StrategyPineParseSummary,
} from "../types/reportStrategyPine";
import { extractPineScriptText } from "./reportStrategyPineUi";
import { resolveEntityBrokerAccounts } from "./brokerAccountRef";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function pickString(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v;
  }
  return "";
}

function pickPineFields(record: Record<string, unknown>): ReportStrategyPineFields {
  const fields: ReportStrategyPineFields = {};
  const assign = (key: keyof ReportStrategyPineFields) => {
    const value = record[key as string];
    if (value !== undefined && value !== null) {
      (fields as Record<string, unknown>)[key as string] = value;
    }
  };
  assign("pine_parse_status");
  assign("pine_parse_warnings");
  assign("inject_readiness");
  assign("exit_hook_readiness");
  assign("pine_script_kind");
  assign("pine_version");
  assign("has_composed_pine");
  assign("has_indicator_pine");
  assign("default_compose_mode");
  assign("last_compose_mode");
  assign("active_pine");
  assign("pine_parse_summary");
  return fields;
}

function normalizePineParseSummary(raw: Record<string, unknown>): StrategyPineParseSummary | undefined {
  const ps = raw.pine_parse_summary;
  if (!isRecord(ps)) return undefined;
  const parse_issues_top = Array.isArray(ps.parse_issues_top)
    ? (ps.parse_issues_top as StrategyPineParseSummary["parse_issues_top"])
    : undefined;
  const parse_issue_codes_top = Array.isArray(ps.parse_issue_codes_top)
    ? (ps.parse_issue_codes_top.filter((x) => typeof x === "string") as string[])
    : undefined;
  return {
    pine_parse_status:
      ps.pine_parse_status === "full" || ps.pine_parse_status === "partial" || ps.pine_parse_status === "failed"
        ? ps.pine_parse_status
        : undefined,
    pine_script_kind: typeof ps.pine_script_kind === "string" ? ps.pine_script_kind : undefined,
    pine_version: typeof ps.pine_version === "string" ? ps.pine_version : undefined,
    inject_readiness:
      typeof ps.inject_readiness === "string" ? (ps.inject_readiness as StrategyPineParseSummary["inject_readiness"]) : undefined,
    exit_hook_readiness:
      typeof ps.exit_hook_readiness === "string"
        ? (ps.exit_hook_readiness as StrategyPineParseSummary["exit_hook_readiness"])
        : undefined,
    entry_sites_count: typeof ps.entry_sites_count === "number" ? ps.entry_sites_count : undefined,
    injectable_entry_count: typeof ps.injectable_entry_count === "number" ? ps.injectable_entry_count : undefined,
    exit_sites_count: typeof ps.exit_sites_count === "number" ? ps.exit_sites_count : undefined,
    parse_issues_top,
    parse_issue_codes_top,
    requirements_version: typeof ps.requirements_version === "string" ? ps.requirements_version : undefined,
  };
}

function normalizeActivePine(raw: Record<string, unknown>): StrategyActivePineSummary | undefined {
  const ap = raw.active_pine;
  if (!isRecord(ap)) {
    const status = raw.active_pine_status;
    if (status === "none" || status === "composed" || status === "stale") {
      return {
        status: status as StrategyActivePineStatus,
        compose_stale: Boolean(raw.compose_stale),
      };
    }
    return undefined;
  }
  const status = ap.status;
  if (status !== "none" && status !== "composed" && status !== "stale") return undefined;
  return {
    status: status as StrategyActivePineStatus,
    compose_stale: Boolean(ap.compose_stale),
    last_compose_mode:
      typeof ap.last_compose_mode === "string" ? (ap.last_compose_mode as StrategyActivePineSummary["last_compose_mode"]) : null,
    last_composed_at: typeof ap.last_composed_at === "string" ? ap.last_composed_at : null,
    last_compose_policy_version_ids: Array.isArray(ap.last_compose_policy_version_ids)
      ? (ap.last_compose_policy_version_ids.filter((x) => typeof x === "string") as string[])
      : null,
    last_compose_policy_label:
      typeof ap.last_compose_policy_label === "string" ? ap.last_compose_policy_label : null,
  };
}

/** Unwrap BE shapes like `{ strategy: { name, ... } }` and hoist Pine text fields. */
export function normalizeReportStrategyDetail(raw: unknown): ReportStrategyDetailResponse & Partial<UpdateReportStrategyResult> {
  if (!isRecord(raw)) {
    throw new Error("Invalid strategy response");
  }

  const nested = isRecord(raw.strategy) ? raw.strategy : null;
  const core = nested ?? raw;

  const id = pickString(core.id, raw.id);
  const name = pickString(core.name, raw.name, core.strategy_name, raw.strategy_name);
  const description = pickString(core.description, raw.description);

  const pineText =
    extractPineScriptText(core) ||
    extractPineScriptText(raw) ||
    pickString(core.pine_source, raw.pine_source);

  const policy_versions = Array.isArray(raw.policy_versions)
    ? (raw.policy_versions as ReportStrategyDetailResponse["policy_versions"])
    : Array.isArray(core.policy_versions)
      ? (core.policy_versions as ReportStrategyDetailResponse["policy_versions"])
      : undefined;

  const current_policy_version_id =
    (typeof raw.current_policy_version_id === "string" && raw.current_policy_version_id) ||
    (typeof core.current_policy_version_id === "string" && core.current_policy_version_id) ||
    null;

  const active_pine = normalizeActivePine(raw) ?? normalizeActivePine(core);
  const pine_parse_summary = normalizePineParseSummary(raw) ?? normalizePineParseSummary(core);

  const broker_name =
    pickString(core.broker_name, raw.broker_name, core.brokerName, raw.brokerName) || null;
  const broker_account_code =
    pickString(
      core.broker_account_code,
      raw.broker_account_code,
      core.brokerAccountCode,
      raw.brokerAccountCode
    ) || null;
  const accounts = resolveEntityBrokerAccounts({
    accounts: core.accounts ?? raw.accounts,
    brokerName: broker_name,
    brokerAccountCode: broker_account_code,
  });

  return {
    ...pickPineFields(core),
    ...pickPineFields(raw),
    id,
    name,
    description,
    user_id: pickString(core.user_id, raw.user_id),
    is_archived: Boolean(core.is_archived ?? raw.is_archived),
    broker_name,
    broker_account_code,
    ...(accounts.length > 0 ? { accounts } : {}),
    created_at: pickString(core.created_at, raw.created_at),
    updated_at: pickString(core.updated_at, raw.updated_at),
    pine_source: pineText || null,
    active_pine,
    pine_parse_summary,
    current_policy_version_id,
    policy_versions,
    compose_invalidated: Boolean(raw.compose_invalidated ?? core.compose_invalidated),
    compose_invalidated_reason:
      typeof raw.compose_invalidated_reason === "string"
        ? raw.compose_invalidated_reason
        : typeof core.compose_invalidated_reason === "string"
          ? core.compose_invalidated_reason
          : undefined,
  } as ReportStrategyDetailResponse & {
    compose_invalidated?: boolean;
    compose_invalidated_reason?: string;
  };
}

/** Fill missing name/description from list row when detail omits them. */
export function mergeReportStrategyWithListRow(
  detail: ReportStrategyResponse,
  listRow?: ReportStrategyResponse | null
): ReportStrategyResponse {
  if (!listRow) return detail;
  return {
    ...detail,
    name: detail.name?.trim() ? detail.name : listRow.name ?? "",
    description: detail.description?.trim() ? detail.description : listRow.description ?? "",
    is_archived: detail.is_archived ?? listRow.is_archived,
  };
}
